import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { TasksService } from '../tokens/tasks.token';
import {
  ExecutionTask,
  CompletedTask,
  DashboardStats,
  TaskHistoryParams,
  TaskQueueParams,
  TaskTransferRequest,
  ActiveOfficer,
  WorkflowTask,
} from '../models/task.model';
import { TranslocoService } from '@jsverse/transloco';
import { API_PATHS } from '../config/api-paths';
import { extractBackendErrorMessage } from '../util/backend-error';

/**
 * REST-backed implementation of `TasksService`.
 *
 * Calls the consolidated `TaskResource` at `/services/embedded/api/tasks`.
 * All requests are authenticated via the portal's `authInterceptor`.
 *
 * Rule 09 — every mutating method (claim, release, complete, saveDraft,
 * transfer) surfaces backend error messages via `extractBackendErrorMessage`.
 *
 * Rule 10 — no mock fallback, no internal in-memory state.
 * Playwright tests intercept the HTTP layer via `page.route()`.
 */
@Injectable()
export class TasksRestService implements TasksService {
  private readonly http = inject(HttpClient);
  private readonly transloco = inject(TranslocoService);

  private get base(): string {
    return `${API_PATHS.EMBEDDED_API_BASE}/tasks`;
  }

  /**
   * Resolves a backend error to a display string, translating any `error.xxx`
   * i18n key the backend returns into the active language (Rule 09). The
   * `fallbackKey` is itself translated so callers never see a raw key when the
   * backend gives us nothing usable.
   */
  private resolveError(err: HttpErrorResponse, fallbackKey: string): string {
    return extractBackendErrorMessage(err, this.transloco.translate(fallbackKey), key =>
      this.transloco.translate(key),
    );
  }

  /**
   * The single error path for every mutating method — Rule 09 + Rule 23, and Rule 17 (one helper,
   * not six copies of the same `catchError`).
   *
   * <p>It rethrows the ORIGINAL {@link HttpErrorResponse} with a resolved `code` attached, rather
   * than a fresh `{ code }` literal. Replacing the response used to discard `err.error` — the RFC
   * 7807 body — which quietly made two things impossible for every caller:
   * <ul>
   *   <li>Rule 09's "show the backend's own words": `extractBackendErrorMessage` found no body and
   *       fell through to the caller's generic fallback, so a 422 with a precise `detail` surfaced
   *       as "Verdict could not be submitted".</li>
   *   <li>Rule 23's "backend `fieldErrors[]` land back ON the field": the array was gone, so a
   *       named offending field could only ever be reported in the banner.</li>
   * </ul>
   * Attaching `code` keeps the existing `err.code` readers working unchanged.
   */
  private fail(err: HttpErrorResponse, fallbackKey: string): Observable<never> {
    return throwError(() => Object.assign(err, { code: this.resolveError(err, fallbackKey) }));
  }

  // ── Queue methods ─────────────────────────────────────────────────────────────

  listRoleQueue(params?: TaskQueueParams): Observable<ExecutionTask[]> {
    return this.http.get<ExecutionTask[]>(this.base, {
      params: this.buildParams({ ...params, queue: 'role' }),
    });
  }

  listMyTasks(params?: TaskQueueParams): Observable<ExecutionTask[]> {
    return this.http.get<ExecutionTask[]>(this.base, {
      params: this.buildParams({ ...params, queue: 'mine' }),
    });
  }

  // ── Single-task methods ───────────────────────────────────────────────────────

  getTask(taskId: string): Observable<ExecutionTask> {
    return this.http.get<ExecutionTask>(`${this.base}/${taskId}`);
  }

  getDossier<T = unknown>(taskId: string, params?: Record<string, string>): Observable<T> {
    let httpParams = new HttpParams();
    if (params) {
      for (const [key, value] of Object.entries(params)) {
        httpParams = httpParams.set(key, value);
      }
    }
    return this.http.get<T>(`${this.base}/${taskId}/dossier`, { params: httpParams });
  }

  // ── Claim / release ───────────────────────────────────────────────────────────

  claim(taskId: string): Observable<ExecutionTask> {
    return this.http.post<ExecutionTask>(`${this.base}/${taskId}/claim`, null).pipe(
      catchError((err: HttpErrorResponse) => this.fail(err, 'errors.tasks.claimFailed')),
    );
  }

  release(taskId: string): Observable<ExecutionTask> {
    return this.http.post<ExecutionTask>(`${this.base}/${taskId}/release`, null).pipe(
      catchError((err: HttpErrorResponse) => this.fail(err, 'errors.tasks.releaseFailed')),
    );
  }

  // ── Draft / complete ──────────────────────────────────────────────────────────

  saveDraft(taskId: string, body: unknown): Observable<void> {
    return this.http.put<void>(`${this.base}/${taskId}/draft`, body).pipe(
      catchError((err: HttpErrorResponse) => this.fail(err, 'errors.tasks.saveDraftFailed')),
    );
  }

  complete<T = unknown>(taskId: string, body: T): Observable<WorkflowTask[]> {
    return this.http.post<WorkflowTask[]>(`${this.base}/${taskId}/complete`, body).pipe(
      catchError((err: HttpErrorResponse) => this.fail(err, 'errors.tasks.completeFailed')),
    );
  }

  // ── Transfer ──────────────────────────────────────────────────────────────────

  transfer(taskId: string, body: TaskTransferRequest): Observable<ExecutionTask> {
    return this.http.post<ExecutionTask>(`${this.base}/${taskId}/transfer`, body).pipe(
      catchError((err: HttpErrorResponse) => this.fail(err, 'errors.tasks.transferFailed')),
    );
  }

  // ── Stats / officers ──────────────────────────────────────────────────────────

  getStats(): Observable<DashboardStats> {
    return this.http.get<DashboardStats>(`${this.base}/stats`);
  }

  getActiveOfficers(): Observable<ActiveOfficer[]> {
    return this.http.get<ActiveOfficer[]>(`${this.base}/officers/active`);
  }

  // ── Completed-task history ────────────────────────────────────────────────────

  listCompleted(params?: TaskHistoryParams): Observable<CompletedTask[]> {
    let p = new HttpParams();
    if (params?.days != null) p = p.set('days', String(params.days));
    if (params?.scope) p = p.set('scope', params.scope);
    if (params?.page != null) p = p.set('page', String(params.page));
    if (params?.size != null) p = p.set('size', String(params.size));
    return this.http
      .get<CompletedTask[]>(`${API_PATHS.EMBEDDED_API_BASE}/task-history`, { params: p })
      .pipe(
        catchError((err: HttpErrorResponse) => this.fail(err, 'errors.tasks.historyFailed')),
      );
  }

  // ── Private helpers ───────────────────────────────────────────────────────────

  /**
   * Multi-value query parameters are emitted as REPEATED keys (`status=A&status=B`), which is
   * what Spring binds to a `Set<…>`. Never comma-join them — the backend would try to convert
   * `"A,B"` into a single enum constant and answer 400.
   */
  private appendMulti(p: HttpParams, key: string, value: string | string[]): HttpParams {
    if (Array.isArray(value)) {
      let next = p;
      for (const v of value) {
        next = next.append(key, v);
      }
      return next;
    }
    return p.set(key, value);
  }

  private buildParams(params: TaskQueueParams & { queue?: string }): HttpParams {
    let p = new HttpParams();
    if (params.queue) p = p.set('queue', params.queue);
    if (params.taskType) p = this.appendMulti(p, 'taskType', params.taskType);
    if (params.status) p = this.appendMulti(p, 'status', params.status);
    if (params.tag) p = this.appendMulti(p, 'tag', params.tag);
    if (params.flow) p = p.set('flow', params.flow);
    if (params.track) p = p.set('track', params.track);
    if (params.applicationType) p = this.appendMulti(p, 'applicationType', params.applicationType);
    // Pagination is OPT-IN on the backend: sending neither `page` nor `size` returns every row.
    // Do not default them here — that would silently truncate every existing caller.
    if (params.page != null) p = p.set('page', String(params.page));
    if (params.size != null) p = p.set('size', String(params.size));
    return p;
  }
}
